/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement} tablist
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<TabsRefs>} */
class TabsComponent extends Component {
  requiredRefs = ['tablist'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#moveTabs();
    this.#setDefaultTab();

    const { signal } = this.#controller;
    this.refs.tablist.addEventListener('click', this.#handleClick, { signal });
    this.refs.tablist.addEventListener('keydown', this.#handleKeyDown, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #moveTabs() {
    const { tablist, tabs } = this.refs;
    if (!tabs?.length || !tablist) return;

    for (const tab of tabs) {
      tablist.appendChild(tab);
    }
  }

  #setDefaultTab() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const isMobile = isMobileBreakpoint();
    const attr = isMobile ? 'data-open-by-default-mobile' : 'data-open-by-default-desktop';
    const defaultIndex = tabs.findIndex((tab) => tab.hasAttribute(attr));

    this.#selectTab(defaultIndex !== -1 ? defaultIndex : 0);
  }

  #handleMediaQueryChange = () => {
    this.#setDefaultTab();
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = /** @type {Element} */ (event.target).closest('[role="tab"]');
    if (!(button instanceof HTMLElement)) return;

    const index = this.refs.tabs?.indexOf(button) ?? -1;
    if (index !== -1) this.#selectTab(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (currentIndex === -1) return;

    let nextIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }

    if (nextIndex !== undefined) {
      const nextTab = tabs[nextIndex];

      if (nextTab) {
        event.preventDefault();
        this.#selectTab(nextIndex);
        nextTab.focus();
      }
    }
  };

  /** @param {number} index */
  #selectTab(index) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    for (const [i, tab] of tabs.entries()) {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
